import { getSceneState } from "../../../stores/sceneStore";
import { devLog } from "../../scene/sceneConstants";

export function logRootsAfterRebuild(bodyTree) {
  try {
    if (typeof window === "undefined" || window.__debugRootsAfterRebuild !== true) return;
    const storeNow = getSceneState();
    const bodiesNow =
      storeNow?.bodies && typeof storeNow.bodies === "object" ? storeNow.bodies : {};
    const worldNow = Array.isArray(storeNow?.worldBodies) ? storeNow.worldBodies : [];
    const bodyKeys = new Set(Object.keys(bodiesNow));
    const childKeys = new Set();
    Object.values(bodiesNow).forEach((b) => {
      const kids = Array.isArray(b?.children) ? b.children : [];
      kids.forEach((c) => {
        if (typeof c === "string" && bodyKeys.has(c)) childKeys.add(c);
      });
    });
    const computedRoots = Array.from(bodyKeys).filter((k) => !childKeys.has(k));
    const declaredRoots = worldNow
      .map((wb) => (typeof wb === "string" ? wb : wb?.uuid))
      .filter((u) => typeof u === "string");
    const rootDetails = computedRoots.slice(0, 12).map((uuid) => {
      const b = bodiesNow?.[uuid];
      return {
        uuid,
        name: b?.name ?? null,
        pos: Array.isArray(b?.position) ? b.position : null,
        quat: Array.isArray(b?.orientation) ? b.orientation : null,
        childCount: Array.isArray(b?.children) ? b.children.length : null,
        jointCount: Array.isArray(b?.joints) ? b.joints.length : null,
      };
    });
    devLog("[Scene] Root debug after rebuild", {
      storeBodyCount: bodyKeys.size,
      worldBodiesCount: worldNow.length,
      declaredRootsCount: declaredRoots.length,
      computedRootsCount: computedRoots.length,
      bodyTreeRootsCount: bodyTree?.length ?? null,
      sampleDeclaredRoots: declaredRoots.slice(0, 8),
      sampleComputedRoots: computedRoots.slice(0, 8),
      rootDetails,
    });
  } catch {
    // Optional debug logging should not affect scene rebuild.
  }
}

export function logParentingAfterRebuild({ bodyTree, jointObjectsRef }) {
  try {
    if (typeof window === "undefined" || window.__debugParentingAfterRebuild !== true) return;
    const storeNow = getSceneState();
    const bodiesNow =
      storeNow?.bodies && typeof storeNow.bodies === "object" ? storeNow.bodies : {};
    const expectedParentByUuid = {};
    Object.values(bodiesNow).forEach((b) => {
      const kids = Array.isArray(b?.children) ? b.children : [];
      kids.forEach((c) => {
        if (typeof c === "string") expectedParentByUuid[c] = b.uuid;
        else if (c && typeof c === "object" && typeof c.uuid === "string")
          expectedParentByUuid[c.uuid] = b.uuid;
      });
    });

    const entries = Object.values(jointObjectsRef.current || {});
    const mismatches = [];
    for (let i = 0; i < entries.length && mismatches.length < 20; i += 1) {
      const e = entries[i];
      const pose = e?.bodyGroup;
      const bodyUuid = pose?.userData?.bodyUuid;
      if (!pose || !bodyUuid) continue;
      const expectedParent = expectedParentByUuid[bodyUuid] || null;

      let p = pose.parent;
      let actualParentPoseUuid = null;
      while (p) {
        const maybe = p?.userData?.bodyUuid;
        const isPose = p?.userData?.isPoseGroup === true;
        if (isPose && maybe && maybe !== bodyUuid) {
          actualParentPoseUuid = maybe;
          break;
        }
        p = p.parent;
      }

      if ((expectedParent || null) !== (actualParentPoseUuid || null)) {
        mismatches.push({
          joint: e?.joint?.name || e?.joint?.uuid || null,
          bodyUuid,
          bodyName: bodiesNow?.[bodyUuid]?.name || pose?.userData?.bodyName || null,
          expectedParent,
          expectedParentName: expectedParent ? bodiesNow?.[expectedParent]?.name || null : null,
          actualParentPoseUuid,
          actualParentName: actualParentPoseUuid
            ? bodiesNow?.[actualParentPoseUuid]?.name || null
            : null,
        });
      }
    }

    devLog("[Scene] Parenting debug after rebuild", {
      checked: entries.length,
      mismatchesCount: mismatches.length,
      mismatches,
      bodyTreeRootsCount: bodyTree?.length ?? null,
    });
  } catch {
    // Optional debug logging should not affect scene rebuild.
  }
}

export function logKinematicsAfterRebuild({ jointObjectsRef, storeNow }) {
  try {
    if (typeof window === "undefined" || window.__debugKinematicsAfterRebuild !== true) return;
    const entries = Object.values(jointObjectsRef.current || {});
    let pivotCount = 0;
    const sample = [];
    for (let i = 0; i < entries.length && sample.length < 12; i += 1) {
      const e = entries[i];
      const bg = e?.bodyGroup;
      const j = e?.joint;
      if (!bg || !j) continue;
      if (bg.userData?._pivotGroup) pivotCount += 1;
      const pivot = bg.userData?._pivotGroup || null;
      const pivotPos = pivot?.position?.toArray?.() || null;
      const pivotQuat = pivot?.quaternion?.toArray?.() || null;
      const storeBody = bg.userData?.bodyUuid ? storeNow?.bodies?.[bg.userData.bodyUuid] : null;
      sample.push({
        joint: j?.name || j?.uuid,
        kind: j?.kind,
        bodyUuid: bg.userData?.bodyUuid || bg.name,
        jointPosLocal: Array.isArray(j?.position) ? j.position : null,
        storeBodyPos: Array.isArray(storeBody?.position) ? storeBody.position : null,
        storeBodyQuat: Array.isArray(storeBody?.orientation) ? storeBody.orientation : null,
        pivotPos,
        pivotQuat,
        localPos: bg.position?.toArray?.() || null,
        localQuat: bg.quaternion?.toArray?.() || null,
      });
    }
    devLog("[Scene] Kinematics after rebuild", {
      jointEntries: entries.length,
      pivotCount,
      sample,
    });
  } catch {
    // Optional debug logging should not affect scene rebuild.
  }
}
